import { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, Database, Variable } from 'lucide-react';
import { useFlowBuilderStore } from '../../stores/flowBuilderStore';

interface VariablePickerProps {
  onInsert: (variable: string) => void;
}

export function VariablePicker({ onInsert }: VariablePickerProps) {
  const { bitrixFields, nodes, edges, selectedNode } = useFlowBuilderStore();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState<'bitrix' | 'custom'>('bitrix');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Reset search when changing selected node so variable sections don't disappear
    // due to stale filters from another node.
    setSearch('');
  }, [selectedNode?.id]);

  const nodesInCurrentPath = useMemo(() => {
    const selectedNodeId = selectedNode?.id;
    if (!selectedNodeId) {
      return null;
    }

    const incomingMap = new Map<string, string[]>();

    for (const edge of edges) {
      const sourceId = String(edge.source || '').trim();
      const targetId = String(edge.target || '').trim();
      if (!sourceId || !targetId) {
        continue;
      }

      if (sourceId === '__instruction_note__' || targetId === '__instruction_note__') {
        continue;
      }

      const existing = incomingMap.get(targetId) || [];
      existing.push(sourceId);
      incomingMap.set(targetId, existing);
    }

    const visited = new Set<string>();
    const stack: string[] = [selectedNodeId];

    while (stack.length > 0) {
      const currentNodeId = stack.pop()!;
      if (visited.has(currentNodeId)) {
        continue;
      }

      visited.add(currentNodeId);
      const parents = incomingMap.get(currentNodeId) || [];
      for (const parentId of parents) {
        if (!visited.has(parentId)) {
          stack.push(parentId);
        }
      }
    }

    return visited;
  }, [edges, selectedNode?.id]);

  const hasCrmUpstreamNode = useMemo(() => {
    if (!nodesInCurrentPath) return false;
    return nodes.some((node) => {
      if (!nodesInCurrentPath.has(node.id)) return false;
      if (node.id === selectedNode?.id) return false;
      const nodeType = String((node.data as any)?.type || '').trim().toUpperCase();
      if (nodeType === 'SEARCH_CONTACT' || nodeType === 'GET_CRM_ENTITY') return true;
      if (nodeType === 'UPDATE_CONTACT') {
        const mode = String((node.data as any)?.config?.mode || '').trim().toLowerCase();
        return mode === 'get_crm_entity';
      }
      return false;
    });
  }, [nodes, nodesInCurrentPath, selectedNode?.id]);

  const filteredFields = useMemo(() => {
    if (!bitrixFields) return [];

    const term = search.toLowerCase();
    const result = [];

    // Add Open Channel variables
    const openChannelVars = [
      { key: 'sender_name', label: 'Nome do Chat' },
      { key: 'sender_phone', label: 'Telefone do Cliente' },
      { key: 'dialog_id', label: 'ID do Diálogo' },
      { key: 'message_text', label: 'Última Mensagem do Cliente' },
      { key: 'image_url', label: 'URL da Imagem Recebida' },
      { key: 'image_file_url', label: 'URL da Imagem Recebida (alias)' },
      { key: 'image_file_base64', label: 'Imagem Recebida (Base64 — para campo File do CRM)' },
      { key: 'image_file_mime_type', label: 'Tipo da Imagem Recebida' },
      { key: 'has_image_file', label: 'Mensagem Tem Imagem' },
      { key: 'document_file_url', label: 'URL do Documento Recebido' },
      { key: 'pdf_file_url', label: 'URL do PDF Recebido (alias)' },
      { key: 'document_file_base64', label: 'Documento Recebido (Base64 — para campo File do CRM)' },
      { key: 'document_file_name', label: 'Nome do Documento Recebido' },
      { key: 'document_file_mime_type', label: 'Tipo do Documento Recebido' },
      { key: 'has_document_file', label: 'Mensagem Tem Documento' },
      { key: 'audio_file_url', label: 'URL do Áudio Recebido' },
    ];

    const filteredOpenChannel = openChannelVars.filter(v => 
      v.label.toLowerCase().includes(term) || v.key.toLowerCase().includes(term)
    );

    if (filteredOpenChannel.length > 0) {
      result.push({
        entity: 'Canal Aberto',
        prefix: null, // No prefix for these
        fields: filteredOpenChannel.map(v => ({
          key: v.key,
          label: v.label,
          type: 'string'
        }))
      });
    }

    if (hasCrmUpstreamNode) {
      const entities = [
        { key: 'contact', label: 'Contato' },
        { key: 'lead', label: 'Lead' },
        { key: 'deal', label: 'Negócio' },
        { key: 'company', label: 'Empresa' },
      ];

      for (const entity of entities) {
        const fields = bitrixFields[entity.key];
        if (!fields) continue;

        const matches = Object.entries(fields).filter(([key, field]: [string, any]) => {
          const title = (field.formLabel || field.title || key).toLowerCase();
          return title.includes(term) || key.toLowerCase().includes(term);
        });

        if (matches.length > 0) {
          result.push({
            entity: entity.label,
            prefix: entity.key,
            fields: matches.map(([key, field]: [string, any]) => ({
              key,
              label: field.formLabel || field.title || key,
              type: field.type
            }))
          });
        }
      }
    }

    return result;
  }, [bitrixFields, search, hasCrmUpstreamNode]);

  // Extract custom variables from the current flow's nodes
  const allCustomVariables = useMemo(() => {
    const vars: Array<{ name: string; source: string; subKeys?: string[] }> = [];
    const selectedNodeId = selectedNode?.id;

    const normalizeVariableName = (value: unknown): string => {
      return String(value ?? '')
        .replace(/^\s*\{\{\s*/, '')
        .replace(/\s*\}\}\s*$/, '')
        .trim()
        .replace(/\s+/g, '_');
    };

    for (const node of nodes) {
      // Nunca listar variáveis geradas pelo próprio nó em edição.
      if (selectedNodeId && node.id === selectedNodeId) {
        continue;
      }

      const data = node.data as any;
      const config = (data?.config || {}) as Record<string, any>;
      const nodeType = String(data?.type || '').trim().toUpperCase();
      const nodeMode = String(config?.mode || data?.mode || '').trim().toLowerCase();
      const variableNameCandidate = normalizeVariableName(
        config?.variableName ?? data?.variableName,
      );
      const isSetVariableNode =
        nodeType === 'SET_VARIABLE' ||
        nodeMode === 'set_variable' ||
        (!!variableNameCandidate &&
          (Object.prototype.hasOwnProperty.call(config, 'value')
            || Object.prototype.hasOwnProperty.call(data || {}, 'value')));
      const isNodeInCurrentPath = !nodesInCurrentPath || nodesInCurrentPath.has(node.id);

      // Regra: apenas variáveis do nó "Definir Variável" são globais.
      // Todas as demais variáveis personalizadas só aparecem se o nó estiver no mesmo caminho.
      if (!isSetVariableNode && !isNodeInCurrentPath) {
        continue;
      }

      if (isSetVariableNode && variableNameCandidate) {
        vars.push({
          name: variableNameCandidate,
          source: `Definir Variável (global)`,
        });
      }

      if (nodeType === 'WAIT_INPUT' && config.variableName) {
        vars.push({
          name: normalizeVariableName(config.variableName),
          source: `Aguardar Entrada`,
        });
      }

      if (nodeType === 'AI_RESPONSE' && config.responseVariable) {
        vars.push({
          name: normalizeVariableName(config.responseVariable),
          source: `IA`,
        });
      }

      if (nodeType === 'PARSE_JSON') {
        const outputVariable = normalizeVariableName(config.outputVariable || 'parsed_json');
        if (outputVariable) {
          vars.push({
            name: outputVariable,
            source: 'Parser JSON',
          });
        }
      }

      if (nodeType === 'EXTRACT_DATA') {
        const sourceLabel = 'Extrair Dado';
        const outputVariable = normalizeVariableName(config.outputVariable || 'extracted_value');
        if (outputVariable) {
          vars.push(
            { name: outputVariable, source: sourceLabel },
            { name: `${outputVariable}_raw`, source: sourceLabel },
            { name: `${outputVariable}_found`, source: sourceLabel },
            { name: `${outputVariable}_type`, source: sourceLabel },
          );
          if (String(config.docOutputFormat || '') === 'both') {
            vars.push({ name: `${outputVariable}_formatted`, source: sourceLabel });
          }
        }
      }

      if (nodeType === 'AI_TRANSCRIBE_AUDIO') {
        const transcribeVar = normalizeVariableName(config.responseVariable || 'audio_transcript');
        vars.push({
          name: transcribeVar,
          source: `IA Transcrever Áudio`,
        });
      }

      if (nodeType === 'AI_ANALYZE_IMAGE' && config.responseVariable) {
        vars.push({
          name: normalizeVariableName(config.responseVariable),
          source: `IA Analisar Imagem`,
        });
      }

      if (nodeType === 'HTTP_REQUEST' && config.responseVariable) {
        // If the request was tested, responseFields holds the detected JSON paths
        // (e.g. data.Nome, data.Telefone) — suggest those instead of a generic "data".
        const detectedFields = Array.isArray(config.responseFields)
          ? config.responseFields.filter((f: any) => typeof f === 'string' && f.trim())
          : [];
        vars.push({
          name: normalizeVariableName(config.responseVariable),
          source: `Requisição HTTP`,
          subKeys: detectedFields.length > 0 ? detectedFields : ['data', 'status', 'headers'],
        });
      }

      if (nodeType === 'SEARCH_CONTACT') {
        const sourceLabel = 'Buscar Cliente';
        const searchIn = String(config.searchIn || '').trim().toLowerCase();
        const isDealSearch = searchIn === 'deal';
        const isLeadSearch = searchIn === 'lead';
        const configuredIdVariable = normalizeVariableName(config.idVariable || 'entity_id');

        vars.push(
          { name: 'entity_id', source: sourceLabel },
          { name: 'entity_type', source: sourceLabel },
          { name: 'entity_name', source: sourceLabel },
        );

        if (configuredIdVariable && configuredIdVariable !== 'entity_id') {
          vars.push({
            name: configuredIdVariable,
            source: sourceLabel,
          });
        }

        if (isDealSearch) {
          vars.push({
            name: 'deal_id',
            source: sourceLabel,
          });
        } else if (isLeadSearch) {
          vars.push(
            { name: 'lead_id', source: sourceLabel },
            { name: 'lead.NAME', source: sourceLabel },
            { name: 'lead.LAST_NAME', source: sourceLabel },
            { name: 'lead.PHONE', source: sourceLabel },
            { name: 'lead.EMAIL', source: sourceLabel },
            { name: 'lead.STATUS_ID', source: sourceLabel },
            { name: 'lead.SOURCE_ID', source: sourceLabel },
          );
        } else {
          vars.push(
            { name: 'contact_id', source: sourceLabel },
            { name: 'company_id', source: sourceLabel },
          );
        }
      }

      if (
        nodeType === 'CREATE_COMPANY'
        || nodeType === 'CREATE_CONTACT'
        || nodeType === 'CREATE_DEAL'
        || nodeType === 'CREATE_LEAD'
      ) {
        const mode = nodeType === 'CREATE_CONTACT'
          ? 'create_contact'
          : nodeType === 'CREATE_DEAL'
            ? 'create_deal'
            : nodeType === 'CREATE_COMPANY'
              ? 'create_company'
              : String(config.mode || '').trim().toLowerCase();

        const createSettings =
          mode === 'create_contact'
            ? {
                source: 'Criar Contato',
                defaultIdVariable: 'contact_id',
              }
            : mode === 'create_deal'
              ? {
                  source: 'Criar Negócio',
                  defaultIdVariable: 'deal_id',
                }
              : mode === 'create_company'
                ? {
                    source: 'Criar Empresa',
                    defaultIdVariable: 'company_id',
                  }
                : null;

        if (createSettings) {
          vars.push(
            { name: 'entity_id', source: createSettings.source },
            { name: 'entity_type', source: createSettings.source },
            { name: 'entity_name', source: createSettings.source },
            { name: createSettings.defaultIdVariable, source: createSettings.source },
          );

          if (config.responseVariable) {
            vars.push({
              name: normalizeVariableName(config.responseVariable),
              source: createSettings.source,
            });
          }
        }
      }

      const updateContactMode = String(config?.mode || '').trim().toLowerCase();
      const isGetCrmEntityNode =
        nodeType === 'GET_CRM_ENTITY'
        || (nodeType === 'UPDATE_CONTACT' && updateContactMode === 'get_crm_entity');

      if (isGetCrmEntityNode) {
        const sourceLabel = 'Obter CRM';
        const responseVariable = normalizeVariableName(config.responseVariable || 'entity');
        const fetchFieldIds = Array.isArray(config.fetchFieldIds)
          ? config.fetchFieldIds.map((value: unknown) => normalizeVariableName(value)).filter(Boolean)
          : [];
        const fetchFieldValueMode = String(config.fetchFieldValueMode || 'both')
          .trim()
          .toLowerCase() === 'code'
          ? 'code'
          : String(config.fetchFieldValueMode || 'both')
              .trim()
              .toLowerCase() === 'label'
            ? 'label'
            : 'both';

        vars.push(
          { name: 'entity_id', source: sourceLabel },
          { name: 'entity_type', source: sourceLabel },
          { name: 'entity_name', source: sourceLabel },
          { name: 'entity_found', source: sourceLabel },
        );

        if (responseVariable) {
          vars.push({
            name: responseVariable,
            source: sourceLabel,
          });
        }

        for (const fieldId of fetchFieldIds) {
          vars.push({
            name: fieldId,
            source: sourceLabel,
          });

          if (fetchFieldValueMode === 'both') {
            vars.push(
              {
                name: `${fieldId}_label`,
                source: sourceLabel,
              },
              {
                name: `${fieldId}_code`,
                source: sourceLabel,
              },
            );
          }
        }
      }

      if (nodeType === 'UPDATE_CONTACT' || nodeType === 'MOVE_DEAL_STAGE') {
        const mode = nodeType === 'MOVE_DEAL_STAGE'
          ? 'move_deal_stage'
          : String(config.mode || '').trim().toLowerCase();
        if (mode === 'get_crm_entity') {
          continue;
        }
        const isMoveDealMode = mode === 'move_deal_stage';
        const sourceLabel = isMoveDealMode ? 'Mover Etapa/Pipeline' : 'Editar CRM';

        vars.push(
          { name: 'entity_id', source: sourceLabel },
          { name: 'entity_type', source: sourceLabel },
          { name: 'entity_name', source: sourceLabel },
        );

        if (config.responseVariable) {
          vars.push({
            name: normalizeVariableName(config.responseVariable),
            source: sourceLabel,
          });
        }

        if (isMoveDealMode) {
          vars.push(
            { name: 'deal_id', source: sourceLabel },
            { name: 'deal_stage_id', source: sourceLabel },
            { name: 'deal_pipeline_id', source: sourceLabel },
          );
        }
      }
    }

    // Deduplicate by name
    const unique = vars.filter(
      (v, i, arr) => arr.findIndex((x) => x.name === v.name) === i
    );

    // Show global "Definir Variável" variables first for quicker access.
    unique.sort((a, b) => {
      const aGlobal = a.source.includes('(global)');
      const bGlobal = b.source.includes('(global)');
      if (aGlobal === bGlobal) return a.name.localeCompare(b.name);
      return aGlobal ? -1 : 1;
    });

    return unique;
  }, [nodes, nodesInCurrentPath, selectedNode?.id]);

  const customVariables = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) {
      return allCustomVariables;
    }

    return allCustomVariables.filter(
      (v) =>
        v.name.toLowerCase().includes(term) ||
        v.source.toLowerCase().includes(term),
    );
  }, [allCustomVariables, search]);

  const renderCustomDropdown = () => (
    <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-80 flex flex-col w-full min-w-[280px]">
      <div className="p-2 border-b border-slate-100">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
          <input
            autoFocus
            type="text"
            placeholder="Buscar variável personalizada..."
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-purple-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="overflow-y-auto flex-1 p-1 custom-scrollbar">
        {customVariables.length === 0 ? (
          <div className="p-4 text-center text-xs text-slate-500">
            Nenhuma variável personalizada encontrada.<br />
            <span className="text-slate-400">Adicione nós como "Definir Variável" (global), "Parser JSON", "Aguardar Entrada" ou "Buscar Cliente" no mesmo caminho do nó atual.</span>
          </div>
        ) : (
          <div>
            <div className="px-2 py-1 text-[10px] font-bold text-purple-400 uppercase tracking-wider bg-purple-50">
              Variáveis do Fluxo
            </div>
            <div className="space-y-0.5 mt-1">
              {customVariables.map((v) => (
                <div key={v.name}>
                  <button
                    onClick={() => {
                      onInsert(`{{${v.name}}}`);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-purple-50 rounded flex items-center justify-between group transition-colors"
                  >
                    <div className="flex flex-col">
                      <span className="text-slate-700 font-medium">{v.name}</span>
                      <span className="text-[10px] text-slate-400">{v.source}</span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono group-hover:text-purple-500">
                      {`{{${v.name}}}`}
                    </span>
                  </button>
                  {v.subKeys && (
                    <div className="ml-4 space-y-0.5">
                      {v.subKeys.map((sub) => (
                        <button
                          key={`${v.name}.${sub}`}
                          onClick={() => {
                            onInsert(`{{${v.name}.${sub}}}`);
                            setIsOpen(false);
                          }}
                          className="w-full text-left px-2 py-1 text-xs hover:bg-purple-50 rounded flex items-center justify-between group transition-colors"
                        >
                          <span className="text-slate-600">.{sub}</span>
                          <span className="text-[10px] text-slate-400 font-mono group-hover:text-purple-500">
                            {`{{${v.name}.${sub}}}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (!bitrixFields) {
    return (
      <div className="relative mt-2 space-y-1.5" ref={wrapperRef}>
        <button
          disabled
          className="flex items-center gap-2 text-xs font-medium text-slate-400 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 w-full justify-between cursor-not-allowed"
          title="Variáveis não disponíveis. Verifique a conexão com o Bitrix24."
        >
          <div className="flex items-center gap-2">
              <Database className="w-3 h-3" />
              <span>Carregando variáveis...</span>
          </div>
        </button>
        {allCustomVariables.length > 0 && (
          <>
            <button
              onClick={() => {
                if (isOpen && activeSection === 'custom') { setIsOpen(false); }
                else { setIsOpen(true); setActiveSection('custom'); setSearch(''); }
              }}
              title="Variáveis definidas no fluxo"
              data-testid="custom-variable-picker-button"
              className="flex items-center gap-2 text-xs font-medium text-purple-600 dark:text-purple-300 hover:text-purple-700 dark:hover:text-purple-200 transition-colors px-3 py-2 rounded-md bg-purple-50 dark:bg-purple-500/20 hover:bg-purple-100 dark:hover:bg-purple-500/30 border border-purple-200 dark:border-purple-500/40 w-full justify-between"
            >
              <div className="flex items-center gap-2">
                <Variable className="w-3 h-3" />
                <span>Inserir Variável Personalizada</span>
              </div>
              <ChevronDown className={`w-3 h-3 transition-transform ${isOpen && activeSection === 'custom' ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && activeSection === 'custom' && renderCustomDropdown()}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="relative mt-2 space-y-1.5" ref={wrapperRef}>
      <button
        onClick={() => {
          if (isOpen && activeSection === 'bitrix') { setIsOpen(false); }
          else { setIsOpen(true); setActiveSection('bitrix'); setSearch(''); }
        }}
        className="flex items-center gap-2 text-xs font-medium text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-200 transition-colors px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/30 border border-blue-200 dark:border-blue-500/40 w-full justify-between"
      >
        <div className="flex items-center gap-2">
            <Database className="w-3 h-3" />
            <span>Inserir Variável do Bitrix24</span>
        </div>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen && activeSection === 'bitrix' ? 'rotate-180' : ''}`} />
      </button>

      <button
        onClick={() => {
          if (isOpen && activeSection === 'custom') { setIsOpen(false); }
          else { setIsOpen(true); setActiveSection('custom'); setSearch(''); }
        }}
        title="Variáveis definidas no fluxo"
        data-testid="custom-variable-picker-button"
        className="flex items-center gap-2 text-xs font-medium text-purple-600 dark:text-purple-300 hover:text-purple-700 dark:hover:text-purple-200 transition-colors px-3 py-2 rounded-md bg-purple-50 dark:bg-purple-500/20 hover:bg-purple-100 dark:hover:bg-purple-500/30 border border-purple-200 dark:border-purple-500/40 w-full justify-between"
      >
        <div className="flex items-center gap-2">
          <Variable className="w-3 h-3" />
          <span>Inserir Variável Personalizada</span>
        </div>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen && activeSection === 'custom' ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && activeSection === 'bitrix' && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-80 flex flex-col w-full min-w-[280px]" style={{ bottom: 'auto' }}>
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              <input
                autoFocus
                type="text"
                placeholder="Buscar variável (ex: nome, email)..."
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-blue-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          
          <div className="overflow-y-auto flex-1 p-1 custom-scrollbar">
            {filteredFields.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-500">
                Nenhuma variável encontrada
              </div>
            ) : (
              filteredFields.map((group) => (
                <div key={group.entity} className="mb-2 last:mb-0">
                  <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50">
                    {group.entity}
                  </div>
                  <div className="space-y-0.5 mt-1">
                    {group.fields.map((field) => (
                      <button
                        key={`${group.prefix || 'global'}.${field.key}`}
                        onClick={() => {
                          const variable = group.prefix 
                            ? `{{${group.prefix}.${field.key}}}`
                            : `{{${field.key}}}`;
                          onInsert(variable);
                          setIsOpen(false);
                        }}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 rounded flex items-center justify-between group transition-colors"
                      >
                        <span className="text-slate-700 font-medium truncate mr-2" title={field.label}>
                          {field.label}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono group-hover:text-blue-500">
                          {field.key}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {isOpen && activeSection === 'custom' && renderCustomDropdown()}
    </div>
  );
}
